import { useState, useCallback } from 'react';
import { Handle, Position, type NodeTypes } from '@xyflow/react';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { Input } from '../ui/index.js';

// ==========================================
// Types
// ==========================================

export interface StepWarning {
  message: string;
  severity: 'error' | 'warning';
}

export type FlowDirection = 'vertical' | 'horizontal';

/** Shape of the `data` object WorkflowFlowDiagram.tsx's `buildLayout` builds for a 'step' node. */
export interface StepNodeData {
  label: string;
  stepId: string;
  order: string;
  templateSlug?: string | null;
  executionMode?: string | null;
  optional?: boolean;
  debugStatus?: 'none' | 'has_output' | 'drifted';
  warnings?: StepWarning[];
  hasContract?: boolean;
  approved?: boolean;
  selected: boolean;
  editable: boolean;
  onStepClick?: (stepId: string) => void;
  onUpdateStep?: (stepId: string, updates: { name: string }) => void;
}

/** Shape of the `data` object WorkflowFlowDiagram.tsx's `buildLayout` builds for a 'stepOutput' node. */
export interface OutputNodeData {
  parts: string[];
  parentStepId: string;
  onOutputClick?: (stepId: string) => void;
}

// ==========================================
// Constants
// ==========================================

export const OUTPUT_PART_ICONS: Record<string, string> = {
  summary: '📝',
  table: '📊',
  chart: '📈',
  signal: '📡',
  metric: '📏',
  file: '📁',
  report: '📄',
  schema: '🧬',
  generated_code: '💻',
};

export const modeColors: Record<string, string> = {
  sequential: 'border-info/60 bg-info-muted',
  parallel: 'border-purple/60 bg-purple-muted',
  conditional: 'border-warning/60 bg-warning-muted',
};

// ==========================================
// StepNode — supports inline rename on double-click
// ==========================================

export const StepNodeInner = ({ data, dir }: { data: StepNodeData; dir: FlowDirection }) => {
  const targetPos = dir === 'horizontal' ? Position.Left : Position.Top;
  const sourcePos = dir === 'horizontal' ? Position.Right : Position.Bottom;

  const warnings: StepWarning[] = data.warnings ?? [];
  const errorCount = warnings.filter((w) => w.severity === 'error').length;
  const warnCount = warnings.filter((w) => w.severity === 'warning').length;

  const debugDotColor =
    data.debugStatus === 'has_output' ? 'bg-success' : data.debugStatus === 'drifted' ? 'bg-warning' : null;

  const borderOverride = errorCount > 0 ? 'border-danger/60' : '';

  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState<string>('');

  const startRename = useCallback(() => {
    if (!data.editable) return;
    setDraftName(data.label ?? '');
    setRenaming(true);
  }, [data.editable, data.label]);

  const commitRename = useCallback(() => {
    setRenaming(false);
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== data.label && data.onUpdateStep) {
      data.onUpdateStep(data.stepId, { name: trimmed });
    }
  }, [draftName, data]);

  return (
    <div
      className={`relative px-4 py-3 rounded-lg border-2 shadow-xs min-w-[180px] cursor-pointer transition-all ${
        borderOverride || modeColors[data.executionMode ?? ''] || 'border bg-surface'
      } ${data.selected ? 'ring-2 ring-accent ring-offset-2' : ''} ${data.optional ? 'opacity-75 border-dashed' : ''}`}
      onClick={() => data.onStepClick?.(data.stepId)}
      onDoubleClick={startRename}
    >
      <Handle type="target" position={targetPos} className="bg-faint! w-2! h-2!" />

      {debugDotColor && (
        <div
          className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${debugDotColor} border border-surface`}
          title={data.debugStatus === 'drifted' ? 'Contract changed since last debug' : 'Has debug output'}
        />
      )}

      {errorCount > 0 && (
        <div
          className="absolute -top-3 -left-3"
          title={warnings
            .filter((w) => w.severity === 'error')
            .map((w) => w.message)
            .join('\n')}
        >
          <AlertCircle size={14} className="text-danger" />
        </div>
      )}
      {warnCount > 0 && errorCount === 0 && (
        <div
          className="absolute -top-3 -left-3"
          title={warnings
            .filter((w) => w.severity === 'warning')
            .map((w) => w.message)
            .join('\n')}
        >
          <AlertTriangle size={14} className="text-warning" />
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-mono text-faint">{data.order}</span>
        {renaming ? (
          <Input
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Step name"
            className="h-7 w-full px-1 text-sm font-semibold"
            value={draftName}
            autoFocus
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              }
              if (e.key === 'Escape') {
                setRenaming(false);
              }
              e.stopPropagation(); // prevent ReactFlow from handling Delete/Backspace
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="font-semibold text-sm text-heading truncate"
            title={data.editable ? 'Double-click to rename' : undefined}
          >
            {data.label}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className={`text-xs ${data.templateSlug ? 'text-muted' : 'text-danger italic'}`}>
          {data.templateSlug || 'No template'}
        </span>
      </div>

      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {data.optional && <span className="text-[9px] text-faint italic">optional</span>}
        {data.hasContract === false && (
          <span className="text-[9px] px-1 py-px rounded-sm bg-warning-muted text-warning">no contract</span>
        )}
        {data.approved && (
          <span className="text-[9px] px-1 py-px rounded-sm bg-success-muted text-success">approved</span>
        )}
      </div>

      <Handle type="source" position={sourcePos} className="bg-faint! w-2! h-2!" />
    </div>
  );
};

// ==========================================
// OutputNode
// ==========================================

export const OutputNodeInner = ({ data, dir }: { data: OutputNodeData; dir: FlowDirection }) => {
  const targetPos = dir === 'horizontal' ? Position.Left : Position.Top;
  const parts: string[] = data.parts ?? [];

  if (parts.length === 0) return null;

  return (
    <div
      className="px-3 py-2 rounded-2xl border border-accent/30 bg-surface shadow-xs cursor-pointer"
      onClick={() => data.onOutputClick?.(data.parentStepId)}
    >
      <Handle type="target" position={targetPos} className="bg-faint! w-1.5! h-1.5!" />
      <div className="text-[8px] text-faint font-semibold uppercase tracking-widest mb-1.5">Outputs</div>
      <div className="flex flex-col gap-0.5">
        {parts.map((part) => (
          <div key={part} className="flex items-center gap-1.5 text-[10px] text-muted">
            <span className="w-3 text-center">{OUTPUT_PART_ICONS[part] ?? '•'}</span>
            <span>{part}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// Direction-specific wrappers
// ==========================================

const StepNodeVertical = ({ data }: { data: StepNodeData }) => <StepNodeInner data={data} dir="vertical" />;
const StepNodeHorizontal = ({ data }: { data: StepNodeData }) => <StepNodeInner data={data} dir="horizontal" />;
const OutputNodeVertical = ({ data }: { data: OutputNodeData }) => <OutputNodeInner data={data} dir="vertical" />;
const OutputNodeHorizontal = ({ data }: { data: OutputNodeData }) => <OutputNodeInner data={data} dir="horizontal" />;

export const verticalNodeTypes: NodeTypes = { step: StepNodeVertical, stepOutput: OutputNodeVertical };
export const horizontalNodeTypes: NodeTypes = { step: StepNodeHorizontal, stepOutput: OutputNodeHorizontal };
